import {
    RoamSrtParametricData,
    RoamSrtParametricItem,
    RoamSrtFormularioPayload,
    RoamSrtFormularioTrabajadorPayload
} from '../../../../../services/roamApi';

const RESPONSABLE_TIPO_REGISTRO_TO_ID: Record<string, number> = {
    ruth: 1,
    rugu: 2,
    matricula: 3
};

const RESPONSABLE_ID_TO_TIPO_REGISTRO: Record<number, string> = {
    1: 'ruth',
    2: 'rugu',
    3: 'matricula'
};
const CONSTRUCTION_SITE_TYPE_ID = '7';
const CONSTRUCTION_SITE_CLEAR_PAYLOAD = {
    fechaRecepcion: null,
    fechaInicio: null,
    idProgramaSeg: null,
    superficie: null,
    nroPlantas: null,
    idTipoObra: null,
    idActividadObra: null,
    idEtapaAccidente: null,
    programaSeg: null,
    idEncuadrePrograma: null,
    fechaFin: null,
    fechaSuspension: null,
    fechaReinicio: null
};

const removeNilValues = (value: any): any => {
    if (Array.isArray(value)) {
        return value.map(removeNilValues).filter((item) => item !== null && item !== undefined);
    }

    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value)
                .map(([key, nestedValue]) => [key, removeNilValues(nestedValue)])
                .filter(([, nestedValue]) => nestedValue !== null && nestedValue !== undefined)
        );
    }

    return value;
};

const toNullableString = (value: any) => {
    if (value === null || value === undefined) return null;

    const normalizedValue = String(value).trim();

    return normalizedValue ? normalizedValue : null;
};

const buildDomicilioConNumero = (domicilio: any, numero: any) => {
    const normalizedDomicilio = toNullableString(domicilio);
    const normalizedNumero = toNullableString(numero);

    if (!normalizedDomicilio && !normalizedNumero) return null;
    if (!normalizedDomicilio) return normalizedNumero;
    if (!normalizedNumero) return normalizedDomicilio;

    return `${normalizedDomicilio} ${normalizedNumero}`;
};

const getParametricDescriptionByCode = (
    parametricData: RoamSrtParametricData | undefined,
    sourceKey: 'zonasCuerpo' | 'naturalezaLesiones' | 'formasAccidentes' | 'agentesCausantes',
    code: any
) => {
    if (!parametricData || code === null || code === undefined || code === '') {
        return '';
    }

    const matchedItem = (parametricData[sourceKey] || []).find(
        (item: RoamSrtParametricItem) => String(item.id) === String(code)
    );

    return matchedItem?.descripcion || matchedItem?.nombre || '';
};

const parseDomicilioConNumero = (value: any) => {
    const normalizedValue = toNullableString(value);

    if (!normalizedValue) {
        return {
            domicilioLugar: undefined,
            numeroLugar: undefined
        };
    }

    const match = normalizedValue.match(/^(.*?)(?:\s+(\S+))?$/);

    if (!match) {
        return {
            domicilioLugar: normalizedValue,
            numeroLugar: undefined
        };
    }

    return {
        domicilioLugar: match[1]?.trim() || normalizedValue,
        numeroLugar: match[2]?.trim() || undefined
    };
};

const parseAntiguedadValue = (value: any, cantidadValue?: any) => {
    if (typeof value === 'boolean') {
        return {
            antiguedadPuestoCantidad:
                cantidadValue === undefined || cantidadValue === null
                    ? undefined
                    : String(cantidadValue),
            antiguedadPuestoUnidad: value ? 'true' : 'false'
        };
    }

    const normalizedValue = toNullableString(value);

    if (!normalizedValue) {
        return {
            antiguedadPuestoCantidad: undefined,
            antiguedadPuestoUnidad: undefined
        };
    }

    const match = normalizedValue.match(/^(\d+)\s*(dias|días|meses)$/i);

    if (!match) {
        return {
            antiguedadPuestoCantidad: undefined,
            antiguedadPuestoUnidad: undefined
        };
    }

    return {
        antiguedadPuestoCantidad: match[1],
        antiguedadPuestoUnidad: /meses/i.test(match[2]) ? 'false' : 'true'
    };
};

const toNullableNumber = (value: any) => {
    if (value === null || value === undefined || value === '') return null;

    const normalizedValue = Number(value);

    return Number.isFinite(normalizedValue) ? normalizedValue : null;
};

const toNullableIsoDate = (value: any) => {
    const normalizedValue = toNullableString(value);

    if (!normalizedValue) return null;

    const date = new Date(`${normalizedValue}T00:00:00`);

    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const toNullableIsoDateTime = (dateValue: any, timeValue?: any) => {
    const normalizedDate = toNullableString(dateValue);
    if (!normalizedDate) return null;

    const normalizedTime = toNullableString(timeValue);
    const fullValue = normalizedTime
        ? `${normalizedDate}T${normalizedTime.length === 5 ? `${normalizedTime}:00` : normalizedTime}`
        : `${normalizedDate}T00:00:00`;
    const date = new Date(fullValue);

    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const toNullableBoolean = (value: any) => {
    if (value === 'si' || value === 'masculino' || value === 'true' || value === true) return true;
    if (value === 'no' || value === 'femenino' || value === 'false' || value === false)
        return false;

    return null;
};

const hasMeaningfulValue = (value: any) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
};

const buildArrayPayload = <T>(
    rows: Record<string, any>[] | undefined,
    mapper: (row: Record<string, any>) => T
) =>
    (rows || [])
        .filter((row) => Object.values(row || {}).some(hasMeaningfulValue))
        .slice(0, 10)
        .map(mapper);

const buildTrabajadorPayload = (values: Record<string, any>, siniestro?: any) => {
    const workerLocalityId =
        values.idLocalidadTrabajador ||
        siniestro?.accidentado?.datosContacto?.idLocalidad ||
        siniestro?.accidentado?.datosContacto?.localidad?.id ||
        null;
    const nacionalidadId = toNullableNumber(values.nacionalidad);

    return {
        nroSiniestro: toNullableString(values.nroSiniestroArt),
        nroDenuncia: toNullableString(values.nroDenuncia),
        nombre: toNullableString(values.nombresTrabajador),
        apellido: toNullableString(values.apellidoTrabajador),
        cuil: toNullableString(values.cuilTrabajador),
        fechaAccidente: toNullableIsoDate(values.fechaAccidente),
        horaAccidente: toNullableIsoDateTime(values.fechaAccidente, values.horaAccidente),
        descripcion: toNullableString(values.descripcion),
        fechaNacimiento: toNullableIsoDate(values.fechaNacimiento),
        sexo: toNullableBoolean(values.sexo),
        idNacionalidad: nacionalidadId,
        otrosNacionalidad: nacionalidadId === 7 ? toNullableString(values.otrosNacionalidad) : null,
        telefono: toNullableString(values.telefonoTrabajador),
        calle: toNullableString(values.domicilioTrabajador),
        idLocalidad: toNullableNumber(workerLocalityId),
        idCiuo: toNullableNumber(values.codigoCiuo),
        antiguedadUnidad: toNullableBoolean(values.antiguedadPuestoUnidad),
        antiguedadValor: toNullableNumber(values.antiguedadPuestoCantidad),
        idTipoContrato: toNullableNumber(values.codigoTipoContrato)
    } satisfies Partial<RoamSrtFormularioTrabajadorPayload>;
};

const buildEmpleadorPayload = (values: Record<string, any>) => ({
    razonSocial: toNullableString(values.razonSocialEmpleador),
    cuit: toNullableString(values.cuitDatosEmpleador),
    telefono: toNullableString(values.telefonoEmpleador),
    domicilio: toNullableString(values.domicilioEmpleador),
    idLocalidad: toNullableNumber(values.idLocalidadEmpleador),
    dotacionActual: toNullableNumber(values.dotacionActual),
    idGrupoFiscalizacion: toNullableNumber(values.grupoFiscalizacion),
    actividadEconomica: toNullableString(values.actividadEconomicaPrincipal),
    idCodigoCiiu: toNullableNumber(values.codigoCiiuEmpleador),
    idOrganizacion: toNullableNumber(values.denominacionArtEa)
});

const buildLugarPayload = (values: Record<string, any>) => ({
    calle: toNullableString(values.domicilioLugar),
    numero: toNullableString(values.numeroLugar),
    telefono: toNullableString(values.telefonoLugar),
    idLocalidad: toNullableNumber(values.idLocalidadLugar),
    cpa: toNullableString(values.lugarCPA),
    razonSocialCentro: toNullableString(values.razonSocialCentro),
    cuit: toNullableString(values.cuitCentro),
    actividadEconomica: toNullableString(values.actividadEconomicaCentro),
    idCodigoCiiu: toNullableNumber(values.codigoCiiuCentro),
    denominacionArt: toNullableString(values.denominacionArtCentro),
    codigoArt: toNullableString(values.codigoArtCentro),
    idGrupoFiscalizacion: toNullableNumber(values.grupoFiscalizacionCentro),
    otrosFiscalizacion:
        toNullableNumber(values.grupoFiscalizacionCentro) === 7
            ? toNullableString(values.otrosFiscalizacion)
            : null,
    numeroEstablecimiento: toNullableString(values.nroEstablecimientoObra),
    dotacionActual: toNullableNumber(values.dotacionActualCentro),
    idTipoLugar: toNullableNumber(values.tipoLugar),
    tipoEstablecimiento: toNullableBoolean(values.tipoEstablecimiento),
    otrosDatos: toNullableString(values.otrosDatosLugar),
    fechaRecepcion: toNullableIsoDate(values.fechaRecepcionAvisoObra),
    fechaInicio: toNullableIsoDate(values.fechaDeclaradaInicio),
    idProgramaSeg: toNullableNumber(values.programaSeguridadObra),
    superficie: toNullableNumber(values.superficieM2),
    nroPlantas: toNullableNumber(values.numeroPlantas),
    idTipoObra: toNullableNumber(values.tipoObra),
    idActividadObra: toNullableNumber(values.actividadesEmpleador),
    idEtapaAccidente: toNullableNumber(values.etapaAccidente),
    programaSeg: toNullableBoolean(values.programaSeguridad),
    idEncuadrePrograma: toNullableNumber(values.encuadrePrograma),
    fechaFin: toNullableIsoDate(values.fechaFinalizacionActividad),
    fechaSuspension: toNullableIsoDate(values.fechaSuspensionObra),
    fechaReinicio: toNullableIsoDate(values.fechaReinicioObra)
});

const buildCodificacionPayload = (
    values: Record<string, any>,
    parametricData?: RoamSrtParametricData
) => ({
    zonaCuerpoAfectada: toNullableString(
        values.zonaCuerpoDescripcion ||
            getParametricDescriptionByCode(parametricData, 'zonasCuerpo', values.codigoZonaCuerpo)
    ),
    idZonaCuerpoAfectada: toNullableNumber(values.codigoZonaCuerpo),
    naturalezaLesion: toNullableString(
        values.naturalezaLesionDescripcion ||
            getParametricDescriptionByCode(
                parametricData,
                'naturalezaLesiones',
                values.codigoNaturalezaLesion
            )
    ),
    idNaturalezaLesion: toNullableNumber(values.codigoNaturalezaLesion),
    formaAccidente: toNullableString(
        values.formaAccidenteDescripcion ||
            getParametricDescriptionByCode(
                parametricData,
                'formasAccidentes',
                values.codigoFormaAccidente
            )
    ),
    idFormaAccidente: toNullableNumber(values.codigoFormaAccidente),
    agenteCausante: toNullableString(
        values.agenteCausanteDescripcion ||
            getParametricDescriptionByCode(
                parametricData,
                'agentesCausantes',
                values.codigoAgenteCausante
            )
    ),
    idAgenteCausante: toNullableNumber(values.codigoAgenteCausante)
});

const buildAccidentePayload = (values: Record<string, any>) => ({
    fecha: toNullableIsoDate(values.fechaDatosAccidente),
    hora: toNullableIsoDateTime(values.fechaDatosAccidente, values.horaDatosAccidente),
    turnoRotativo: toNullableBoolean(values.turnoRotativo),
    horarioHabitualInicio: toNullableIsoDateTime(
        values.fechaDatosAccidente,
        values.horarioHabitualDesde
    ),
    horarioHabitualFin: toNullableIsoDateTime(
        values.fechaDatosAccidente,
        values.horarioHabitualHasta
    ),
    horasExtras: toNullableBoolean(values.realizabaHorasExtras),
    tareasHabituales: toNullableString(values.tareasHabituales),
    tareasMomentoAccidente: toNullableString(values.tareasMomentoAccidente),
    descripcionAccidente: toNullableString(values.descripcionAccidente),
    hechos: toNullableString(values.hechosNecesarios),
    observaciones: toNullableString(values.observaciones)
});

const buildResponsablePayload = (values: Record<string, any>) => ({
    apellido: toNullableString(values.responsableApellido),
    nombre: toNullableString(values.responsableNombres),
    titulo: toNullableString(values.responsableTitulo),
    idTipoMatricula: values.responsableTipoRegistro
        ? RESPONSABLE_TIPO_REGISTRO_TO_ID[values.responsableTipoRegistro] || null
        : null,
    numeroMatricula: toNullableString(values.responsableNumeroRegistro),
    colegio: toNullableString(values.responsableColegio),
    fecha: toNullableIsoDate(values.responsableFecha),
    firmado: toNullableBoolean(values.responsableFirmador)
});

const buildTestigosPayload = (values: Record<string, any>) =>
    buildArrayPayload(values.testigos, (row) => ({
        nombre: toNullableString(row.apellidoNombres),
        tipoDocumento: toNullableString(row.tipoDocumento),
        cuil: toNullableString(row.dniCuil),
        cargo: toNullableString(row.cargo),
        fecha: toNullableIsoDate(row.fecha)
    }));

const buildOtrosAfectadosPayload = (values: Record<string, any>) =>
    buildArrayPayload(values.otrosAccidentados, (row) => ({
        nombre: toNullableString(row.apellidoNombres),
        cuil: toNullableString(row.cuil),
        tipoLesion: toNullableString(row.tipoLesion),
        numeroDenuncia: toNullableString(row.nroDenuncia),
        patologiaTrazadora: toNullableString(row.patologiaTrazadora),
        descripcionLesion: toNullableString(row.descripcionLesion)
    }));

const buildMedidasSeguimientoPayload = (values: Record<string, any>) => {
    const rows = (values.medidasCorrectivas || [])
        .filter((row: Record<string, any>) => hasMeaningfulValue(row?.hecho))
        .slice(0, 10);

    return rows.map((row: Record<string, any>) => {
        const numeroCausa = toNullableNumber(row.nroCausa);
        const medidas = (row.medidas || []).filter((medida: Record<string, any>) =>
            hasMeaningfulValue(medida?.medidaCorrectiva)
        );
        const hasExistingMeasure = medidas.length > 0;
        const requiereMedidaCorrectiva =
            row.requiereMedidaCorrectiva === 'si' ||
            (row.requiereMedidaCorrectiva === undefined && hasExistingMeasure);
        const seguimientos = buildArrayPayload(values.seguimientos, (seguimientoRow) =>
            String(seguimientoRow.medida) === String(row.nroCausa)
                ? {
                      fecha1: toNullableIsoDate(seguimientoRow.fechaPrevista),
                      fecha3: toNullableIsoDate(seguimientoRow.fechaVerificacion),
                      resultadoSeguimiento: toNullableString(seguimientoRow.resultado)
                  }
                : null
        ).filter(Boolean);

        return {
            numeroCausa,
            hecho: toNullableString(row.hecho),
            ...(requiereMedidaCorrectiva
                ? {
                      medidas: medidas.slice(0, 10).map((medida: Record<string, any>) => ({
                          nroMedida: toNullableNumber(medida.nroMedida),
                          medidaCorrectiva: toNullableString(medida.medidaCorrectiva),
                          fecha1: toNullableIsoDate(medida.fechaInicio),
                          fecha2: toNullableIsoDate(medida.fechaFin)
                      })),
                      seguimientos: seguimientos.length ? seguimientos : undefined
                  }
                : {})
        };
    });
};

const buildEntrevistadosPayload = (values: Record<string, any>) =>
    buildArrayPayload(values.entrevistados, (row) => ({
        nombre: toNullableString(row.apellidoNombres),
        tipoDocumento: toNullableString(row.tipoDocumento),
        cuil: toNullableString(row.dniCuil),
        cargo: toNullableString(row.cargo),
        fecha: toNullableIsoDate(row.fecha),
        firmado: toNullableBoolean(row.firmo)
    }));

export const buildCreateSrtPayload = (
    values: Record<string, any>,
    siniestro?: any,
    parametricData?: RoamSrtParametricData
): RoamSrtFormularioPayload => {
    const payload = removeNilValues({
        trabajador: buildTrabajadorPayload(values, siniestro),
        empleador: buildEmpleadorPayload(values),
        lugar: buildLugarPayload(values),
        codificacion: buildCodificacionPayload(values, parametricData),
        accidente: buildAccidentePayload(values),
        responsable: buildResponsablePayload(values),
        testigos: buildTestigosPayload(values),
        otrosAfectados: buildOtrosAfectadosPayload(values),
        medidasSeguimiento: buildMedidasSeguimientoPayload(values),
        entrevistados: buildEntrevistadosPayload(values)
    });

    if (String(values.tipoLugar ?? '') !== CONSTRUCTION_SITE_TYPE_ID) {
        payload.lugar = {
            ...(payload.lugar || {}),
            ...CONSTRUCTION_SITE_CLEAR_PAYLOAD
        };
    }

    return payload;
};

const toInputDateValue = (value: any) => {
    if (!value) return '';
    if (typeof value === 'string') return value.includes('T') ? value.split('T')[0] : value;

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().split('T')[0];
};

const toInputTimeValue = (value: any) => {
    if (!value) return '';
    if (typeof value === 'string') {
        if (value.includes('T')) return value.split('T')[1]?.slice(0, 5) || '';
        return value.slice(0, 5);
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().slice(11, 16);
};

const mapRows = (rows: any[] | undefined, mapper: (item: any) => Record<string, any>) =>
    Array.isArray(rows) && rows.length > 0 ? rows.map(mapper) : undefined;

const readValue = (source: any, nestedValue: any, flatKey: string) =>
    nestedValue !== undefined && nestedValue !== null ? nestedValue : source?.[flatKey];

const mapIfPresent = <T>(value: any, mapper: (input: any) => T) =>
    value === undefined || value === null ? undefined : mapper(value);

export const inferInterviewedDocumentType = (value: any) => {
    const documentLength = String(value ?? '').trim().length;

    if (documentLength === 0) return '';
    if (documentLength === 8) return 'dni';
    if (documentLength === 11) return 'cuil';

    return documentLength < 10 ? 'dni' : 'cuil';
};

export const mapSrtFormularioToValues = (formulario?: any) => {
    const trabajador = formulario?.trabajador || {};
    const empleador = formulario?.empleador || {};
    const lugar = formulario?.lugar || {};
    const codificacion = formulario?.codificacion || {};
    const accidente = formulario?.accidente || {};
    const responsable = formulario?.responsable || {};
    const antiguedadData = parseAntiguedadValue(
        readValue(
            formulario,
            trabajador?.antiguedadUnidad ?? formulario?.trabajadorAntiguedadUnidad,
            'trabajadorAntiguedad'
        ),
        readValue(
            formulario,
            trabajador?.antiguedadValor ?? formulario?.trabajadorAntiguedadValor,
            'trabajadorCantidadAntiguedad'
        )
    );
    const domicilioLugarData =
        formulario?.lugarCalle !== undefined || formulario?.lugarNumero !== undefined
            ? {
                  domicilioLugar: mapIfPresent(formulario?.lugarCalle, String),
                  numeroLugar: mapIfPresent(formulario?.lugarNumero, String)
              }
            : parseDomicilioConNumero(readValue(formulario, lugar?.domicilio, 'lugarDomicilio'));
    const responsableTipoMatriculaId = readValue(
        formulario,
        responsable?.idTipoMatricula,
        'respIdTipoMatricula'
    );
    const responsableRuthValue = readValue(formulario, responsable?.numeroRuth, 'respNumeroRuth');
    const responsableRiguValue = readValue(formulario, responsable?.numeroRigu, 'respNumeroRigu');
    const responsableMatriculaValue = readValue(
        formulario,
        responsable?.numeroMatricula,
        'respNumeroMatricula'
    );
    const responsableTipoRegistro = responsableTipoMatriculaId
        ? RESPONSABLE_ID_TO_TIPO_REGISTRO[Number(responsableTipoMatriculaId)]
        : responsableRuthValue
          ? 'ruth'
          : responsableRiguValue
            ? 'rugu'
            : responsableMatriculaValue
              ? 'matricula'
              : undefined;
    const responsableNumeroRegistro =
        readValue(formulario, responsable?.numeroMatricula, 'respNumeroMatricula') ||
        responsableRuthValue ||
        responsableRiguValue ||
        responsableMatriculaValue;

    const seguimientosDesdeMedidas = (formulario?.medidasSeguimiento || []).flatMap((item: any) =>
        (item?.seguimientos || []).map((seguimiento: any) => ({
            medida: item?.numeroCausa ? String(item.numeroCausa) : '',
            fechaPrevista: toInputDateValue(seguimiento?.fecha1),
            fechaVerificacion: toInputDateValue(seguimiento?.fecha3),
            resultado: seguimiento?.resultadoSeguimiento || ''
        }))
    );

    return {
        nroSiniestroArt: mapIfPresent(
            readValue(formulario, trabajador?.nroSiniestro, 'trabajadorNroSiniestro'),
            String
        ),
        nroDenuncia: mapIfPresent(
            readValue(formulario, trabajador?.nroDenuncia, 'trabajadorNroDenuncia'),
            String
        ),
        nombresTrabajador: mapIfPresent(
            readValue(formulario, trabajador?.nombre, 'trabajadorNombre'),
            String
        ),
        apellidoTrabajador: mapIfPresent(
            readValue(formulario, trabajador?.apellido, 'trabajadorApellido'),
            String
        ),
        cuilTrabajador: mapIfPresent(
            readValue(formulario, trabajador?.cuil, 'trabajadorCuil'),
            String
        ),
        fechaAccidente: mapIfPresent(
            readValue(formulario, trabajador?.fechaAccidente, 'trabajadorFechaAccidente'),
            toInputDateValue
        ),
        horaAccidente: mapIfPresent(
            readValue(formulario, trabajador?.horaAccidente, 'trabajadorHoraAccidente'),
            toInputTimeValue
        ),
        descripcion: mapIfPresent(
            readValue(formulario, trabajador?.descripcion, 'trabajadorDescripcion'),
            String
        ),
        fechaNacimiento: mapIfPresent(
            readValue(formulario, trabajador?.fechaNacimiento, 'trabajadorFechaNacimiento'),
            toInputDateValue
        ),
        sexo:
            typeof readValue(formulario, trabajador?.sexo, 'trabajadorSexo') === 'boolean'
                ? readValue(formulario, trabajador?.sexo, 'trabajadorSexo')
                    ? 'masculino'
                    : 'femenino'
                : undefined,
        nacionalidad: readValue(formulario, trabajador?.idNacionalidad, 'trabajadorIdNacionalidad')
            ? String(readValue(formulario, trabajador?.idNacionalidad, 'trabajadorIdNacionalidad'))
            : undefined,
        otrosNacionalidad: mapIfPresent(
            readValue(formulario, trabajador?.otrosNacionalidad, 'trabajadorOtrosNacionalidad'),
            String
        ),
        telefonoTrabajador: mapIfPresent(
            readValue(formulario, trabajador?.telefono, 'trabajadorTelefono'),
            String
        ),
        domicilioTrabajador: mapIfPresent(
            readValue(formulario, trabajador?.calle, 'trabajadorCalle'),
            String
        ),
        ciudadTrabajador: mapIfPresent(formulario?.trabajadorLocalidad?.nombre, String),
        provinciaTrabajador: mapIfPresent(
            formulario?.trabajadorLocalidad?.provincia?.nombre,
            String
        ),
        codigoPostalTrabajador: mapIfPresent(formulario?.trabajadorLocalidad?.codigoPostal, String),
        idLocalidadTrabajador: mapIfPresent(
            readValue(
                formulario,
                trabajador?.idLocalidad ?? formulario?.trabajadorLocalidad?.id,
                'trabajadorIdLocalidad'
            ),
            String
        ),
        codigoCiuo: readValue(
            formulario,
            trabajador?.idCiuo ?? formulario?.trabajadorCiuo?.id,
            'trabajadorIdCiuo'
        )
            ? String(
                  readValue(
                      formulario,
                      trabajador?.idCiuo ?? formulario?.trabajadorCiuo?.id,
                      'trabajadorIdCiuo'
                  )
              )
            : undefined,
        ...antiguedadData,
        codigoTipoContrato: readValue(
            formulario,
            trabajador?.idTipoContrato ?? formulario?.trabajadorTipoContrato?.id,
            'trabajadorIdTipoContrato'
        )
            ? String(
                  readValue(
                      formulario,
                      trabajador?.idTipoContrato ?? formulario?.trabajadorTipoContrato?.id,
                      'trabajadorIdTipoContrato'
                  )
              )
            : undefined,
        razonSocialEmpleador: mapIfPresent(
            readValue(formulario, empleador?.razonSocial, 'empleadorRazonSocial'),
            String
        ),
        cuitDatosEmpleador: mapIfPresent(
            readValue(formulario, empleador?.cuit, 'empleadorCuit'),
            String
        ),
        telefonoEmpleador: mapIfPresent(
            readValue(formulario, empleador?.telefono, 'empleadorTelefono'),
            String
        ),
        domicilioEmpleador: mapIfPresent(
            readValue(formulario, empleador?.domicilio, 'empleadorDomicilio'),
            String
        ),
        ciudadEmpleador: mapIfPresent(formulario?.empleadorLocalidad?.nombre, String),
        provinciaEmpleador: mapIfPresent(formulario?.empleadorLocalidad?.provincia?.nombre, String),
        cpaEmpleador: mapIfPresent(formulario?.empleadorLocalidad?.codigoPostal, String),
        idLocalidadEmpleador: mapIfPresent(
            readValue(
                formulario,
                empleador?.idLocalidad ?? formulario?.empleadorLocalidad?.id,
                'empleadorIdLocalidad'
            ),
            String
        ),
        dotacionActual: mapIfPresent(
            readValue(formulario, empleador?.dotacionActual, 'empleadorDotacionActual'),
            String
        ),
        grupoFiscalizacion: readValue(
            formulario,
            empleador?.idGrupoFiscalizacion,
            'empleadorIdGrupoFiscalizacion'
        )
            ? String(
                  readValue(
                      formulario,
                      empleador?.idGrupoFiscalizacion,
                      'empleadorIdGrupoFiscalizacion'
                  )
              )
            : undefined,
        actividadEconomicaPrincipal: mapIfPresent(
            readValue(formulario, empleador?.actividadEconomica, 'empleadorActividadEconomica'),
            String
        ),
        codigoCiiuEmpleador: readValue(formulario, empleador?.idCodigoCiiu, 'empleadorIdCodigoCiiu')
            ? String(readValue(formulario, empleador?.idCodigoCiiu, 'empleadorIdCodigoCiiu'))
            : undefined,
        denominacionArtEa: readValue(
            formulario,
            empleador?.idOrganizacion ??
                formulario?.empleadorOrganizacion?.id ??
                formulario?.organizacion?.id,
            'empleadorIdOrganizacion'
        )
            ? String(
                  readValue(
                      formulario,
                      empleador?.idOrganizacion ??
                          formulario?.empleadorOrganizacion?.id ??
                          formulario?.organizacion?.id,
                      'empleadorIdOrganizacion'
                  )
              )
            : undefined,
        codigoArtEa: mapIfPresent(
            formulario?.empleadorOrganizacion?.codigoAseguradora ??
                formulario?.organizacion?.codigoAseguradora,
            String
        ),
        ...domicilioLugarData,
        telefonoLugar: mapIfPresent(
            readValue(formulario, lugar?.telefono, 'lugarTelefono'),
            String
        ),
        ciudadLugar: mapIfPresent(formulario?.lugarLocalidad?.nombre, String),
        provinciaLugar: mapIfPresent(formulario?.lugarLocalidad?.provincia?.nombre, String),
        cpaLugar: mapIfPresent(formulario?.lugarLocalidad?.codigoPostal, String),
        lugarCPA: mapIfPresent(
            readValue(formulario, lugar?.cpa ?? formulario?.lugarCPA, 'lugarCPA'),
            String
        ),
        idLocalidadLugar: mapIfPresent(
            readValue(
                formulario,
                lugar?.idLocalidad ?? formulario?.lugarLocalidad?.id,
                'lugarIdLocalidad'
            ),
            String
        ),
        razonSocialCentro: mapIfPresent(
            readValue(formulario, lugar?.razonSocialCentro, 'lugarRazonSocialCentro'),
            String
        ),
        cuitCentro: mapIfPresent(readValue(formulario, lugar?.cuit, 'lugarCuit'), String),
        actividadEconomicaCentro: mapIfPresent(
            readValue(formulario, lugar?.actividadEconomica, 'lugarActividadEconomica'),
            String
        ),
        codigoCiiuCentro: readValue(
            formulario,
            lugar?.idCodigoCiiu ?? formulario?.lugarCiiu?.id,
            'lugarIdCodigoCiiu'
        )
            ? String(
                  readValue(
                      formulario,
                      lugar?.idCodigoCiiu ?? formulario?.lugarCiiu?.id,
                      'lugarIdCodigoCiiu'
                  )
              )
            : undefined,
        denominacionArtCentro: mapIfPresent(
            readValue(formulario, lugar?.denominacionArt, 'lugarDenominacionArt'),
            String
        ),
        codigoArtCentro: mapIfPresent(
            readValue(formulario, lugar?.codigoArt, 'lugarCodigoArt'),
            String
        ),
        grupoFiscalizacionCentro: readValue(
            formulario,
            lugar?.idGrupoFiscalizacion,
            'lugarIdGrupoFiscalizacion'
        )
            ? String(
                  readValue(formulario, lugar?.idGrupoFiscalizacion, 'lugarIdGrupoFiscalizacion')
              )
            : undefined,
        otrosFiscalizacion: mapIfPresent(
            readValue(formulario, lugar?.otrosFiscalizacion, 'lugarOtrosFiscalizacion'),
            String
        ),
        nroEstablecimientoObra: mapIfPresent(
            readValue(formulario, lugar?.numeroEstablecimiento, 'lugarNumeroEstablecimiento'),
            String
        ),
        dotacionActualCentro: mapIfPresent(
            readValue(formulario, lugar?.dotacionActual, 'lugarDotacionActual'),
            String
        ),
        tipoLugar: readValue(
            formulario,
            lugar?.idTipoLugar ?? formulario?.lugarTipoLugar?.id,
            'lugarIdTipoLugar'
        )
            ? String(
                  readValue(
                      formulario,
                      lugar?.idTipoLugar ?? formulario?.lugarTipoLugar?.id,
                      'lugarIdTipoLugar'
                  )
              )
            : undefined,
        tipoEstablecimiento:
            typeof readValue(formulario, lugar?.tipoEstablecimiento, 'lugarTipoEstablecimiento') ===
            'boolean'
                ? readValue(formulario, lugar?.tipoEstablecimiento, 'lugarTipoEstablecimiento')
                    ? 'true'
                    : 'false'
                : undefined,
        otrosDatosLugar: mapIfPresent(
            readValue(formulario, lugar?.otrosDatos, 'lugarOtrosDatos'),
            String
        ),
        fechaRecepcionAvisoObra: mapIfPresent(
            readValue(formulario, lugar?.fechaRecepcion, 'lugarFechaRecepcion'),
            toInputDateValue
        ),
        fechaDeclaradaInicio: mapIfPresent(
            readValue(formulario, lugar?.fechaInicio, 'lugarFechaInicio'),
            toInputDateValue
        ),
        programaSeguridadObra: readValue(
            formulario,
            lugar?.idProgramaSeg ??
                lugar?.idProgramaSeguridadObra ??
                formulario?.lugarProgramaSeguridadObra?.id,
            'lugarIdProgramaSeguridadObra'
        )
            ? String(
                  readValue(
                      formulario,
                      lugar?.idProgramaSeg ??
                          lugar?.idProgramaSeguridadObra ??
                          formulario?.lugarProgramaSeguridadObra?.id,
                      'lugarIdProgramaSeguridadObra'
                  )
              )
            : undefined,
        superficieM2: mapIfPresent(
            readValue(formulario, lugar?.superficie, 'lugarSuperficie'),
            String
        ),
        numeroPlantas: mapIfPresent(
            readValue(formulario, lugar?.nroPlantas, 'lugarNroPlantas'),
            String
        ),
        tipoObra: readValue(formulario, lugar?.idTipoObra, 'lugarIdTipoObra')
            ? String(readValue(formulario, lugar?.idTipoObra, 'lugarIdTipoObra'))
            : undefined,
        actividadesEmpleador: readValue(formulario, lugar?.idActividadObra, 'lugarIdActividadObra')
            ? String(readValue(formulario, lugar?.idActividadObra, 'lugarIdActividadObra'))
            : undefined,
        etapaAccidente: readValue(
            formulario,
            lugar?.idEtapaAccidente ?? formulario?.lugarEtapaObra?.id,
            'lugarIdEtapaAccidente'
        )
            ? String(
                  readValue(
                      formulario,
                      lugar?.idEtapaAccidente ?? formulario?.lugarEtapaObra?.id,
                      'lugarIdEtapaAccidente'
                  )
              )
            : undefined,
        programaSeguridad:
            typeof readValue(formulario, lugar?.programaSeg, 'lugarProgramaSeg') === 'boolean'
                ? readValue(formulario, lugar?.programaSeg, 'lugarProgramaSeg')
                    ? 'si'
                    : 'no'
                : readValue(
                        formulario,
                        lugar?.idProgramaSeg ??
                            lugar?.idProgramaSeguridadObra ??
                            formulario?.lugarProgramaSeguridadObra,
                        'lugarIdProgramaSeguridadObra'
                    )
                  ? 'si'
                  : 'no',
        encuadrePrograma: readValue(
            formulario,
            lugar?.idEncuadrePrograma,
            'lugarIdEncuadrePrograma'
        )
            ? String(readValue(formulario, lugar?.idEncuadrePrograma, 'lugarIdEncuadrePrograma'))
            : undefined,
        fechaFinalizacionActividad: mapIfPresent(
            readValue(formulario, lugar?.fechaFin, 'lugarFechaFin'),
            toInputDateValue
        ),
        fechaSuspensionObra: mapIfPresent(
            readValue(formulario, lugar?.fechaSuspension, 'lugarFechaSuspension'),
            toInputDateValue
        ),
        fechaReinicioObra: mapIfPresent(
            readValue(formulario, lugar?.fechaReinicio, 'lugarFechaReinicio'),
            toInputDateValue
        ),
        zonaCuerpoDescripcion: mapIfPresent(
            readValue(formulario, codificacion?.zonaCuerpoAfectada, 'codZonaCuerpoAfectada'),
            String
        ),
        codigoZonaCuerpo: readValue(
            formulario,
            codificacion?.idZonaCuerpoAfectada ?? formulario?.zonaCuerpoAfectada?.id,
            'codIdZonaCuerpoAfectada'
        )
            ? String(
                  readValue(
                      formulario,
                      codificacion?.idZonaCuerpoAfectada ?? formulario?.zonaCuerpoAfectada?.id,
                      'codIdZonaCuerpoAfectada'
                  )
              )
            : undefined,
        naturalezaLesionDescripcion: mapIfPresent(
            readValue(formulario, codificacion?.naturalezaLesion, 'codNaturalezaLesion'),
            String
        ),
        codigoNaturalezaLesion: readValue(
            formulario,
            codificacion?.idNaturalezaLesion ?? formulario?.naturalezaLesion?.id,
            'codIdNaturalezaLesion'
        )
            ? String(
                  readValue(
                      formulario,
                      codificacion?.idNaturalezaLesion ?? formulario?.naturalezaLesion?.id,
                      'codIdNaturalezaLesion'
                  )
              )
            : undefined,
        formaAccidenteDescripcion: mapIfPresent(
            readValue(formulario, codificacion?.formaAccidente, 'codFormaAccidente'),
            String
        ),
        codigoFormaAccidente: readValue(
            formulario,
            codificacion?.idFormaAccidente ?? formulario?.formaAccidente?.id,
            'codIdFormaAccidente'
        )
            ? String(
                  readValue(
                      formulario,
                      codificacion?.idFormaAccidente ?? formulario?.formaAccidente?.id,
                      'codIdFormaAccidente'
                  )
              )
            : undefined,
        agenteCausanteDescripcion: mapIfPresent(
            readValue(formulario, codificacion?.agenteCausante, 'codAgenteCausante'),
            String
        ),
        codigoAgenteCausante: readValue(
            formulario,
            codificacion?.idAgenteCausante ?? formulario?.agenteCausante?.id,
            'codIdAgenteCausante'
        )
            ? String(
                  readValue(
                      formulario,
                      codificacion?.idAgenteCausante ?? formulario?.agenteCausante?.id,
                      'codIdAgenteCausante'
                  )
              )
            : undefined,
        fechaDatosAccidente: mapIfPresent(
            readValue(formulario, accidente?.fecha, 'accidenteFecha'),
            toInputDateValue
        ),
        horaDatosAccidente: mapIfPresent(
            readValue(formulario, accidente?.hora, 'accidenteHora'),
            toInputTimeValue
        ),
        turnoRotativo:
            typeof readValue(formulario, accidente?.turnoRotativo, 'accidenteTurnoRotativo') ===
            'boolean'
                ? readValue(formulario, accidente?.turnoRotativo, 'accidenteTurnoRotativo')
                    ? 'si'
                    : 'no'
                : undefined,
        horarioHabitualDesde: mapIfPresent(
            readValue(
                formulario,
                accidente?.horarioHabitualInicio,
                'accidenteHorarioHabitualInicio'
            ),
            toInputTimeValue
        ),
        horarioHabitualHasta: mapIfPresent(
            readValue(formulario, accidente?.horarioHabitualFin, 'accidenteHorarioHabitualFin'),
            toInputTimeValue
        ),
        realizabaHorasExtras:
            typeof readValue(formulario, accidente?.horasExtras, 'accidenteHorasExtras') ===
            'boolean'
                ? readValue(formulario, accidente?.horasExtras, 'accidenteHorasExtras')
                    ? 'si'
                    : 'no'
                : undefined,
        tareasHabituales: mapIfPresent(
            readValue(formulario, accidente?.tareasHabituales, 'accidenteTareasHabituales'),
            String
        ),
        tareasMomentoAccidente: mapIfPresent(
            readValue(
                formulario,
                accidente?.tareasMomentoAccidente,
                'accidenteTareasMomentoAccidente'
            ),
            String
        ),
        descripcionAccidente: mapIfPresent(
            readValue(formulario, accidente?.descripcionAccidente, 'accidenteDescripcionAccidente'),
            String
        ),
        hechosNecesarios: mapIfPresent(
            readValue(formulario, accidente?.hechos, 'accidenteHechos'),
            String
        ),
        observaciones: mapIfPresent(
            readValue(
                formulario,
                accidente?.observaciones ??
                    accidente?.observacion ??
                    accidente?.obs ??
                    formulario?.observaciones ??
                    formulario?.observacion,
                'accidenteObservaciones'
            ),
            String
        ),
        responsableApellido: mapIfPresent(
            readValue(formulario, responsable?.apellido, 'respApellido'),
            String
        ),
        responsableNombres: mapIfPresent(
            readValue(formulario, responsable?.nombre, 'respNombre'),
            String
        ),
        responsableTitulo: mapIfPresent(
            readValue(formulario, responsable?.titulo, 'respTitulo'),
            String
        ),
        responsableTipoRegistro,
        responsableNumeroRegistro: mapIfPresent(responsableNumeroRegistro, String),
        responsableColegio: mapIfPresent(
            readValue(formulario, responsable?.colegio, 'respColegio'),
            String
        ),
        responsableFecha: mapIfPresent(
            readValue(formulario, responsable?.fecha, 'respFecha'),
            toInputDateValue
        ),
        responsableFirmador:
            typeof readValue(formulario, responsable?.firmado, 'respFirmado') === 'boolean'
                ? readValue(formulario, responsable?.firmado, 'respFirmado')
                    ? 'si'
                    : 'no'
                : '',
        testigos: mapRows(formulario?.testigos, (item) => ({
            apellidoNombres: item?.nombre || '',
            dniCuil: item?.cuil || '',
            cargo: item?.cargo || '',
            fecha: toInputDateValue(item?.fecha)
        })),
        otrosAccidentados: mapRows(formulario?.otrosAfectados, (item) => ({
            apellidoNombres: item?.nombre || '',
            tipoDocumento: inferInterviewedDocumentType(item?.cuil),
            cuil: item?.cuil || '',
            tipoLesion: item?.tipoLesion || '',
            nroDenuncia: item?.numeroDenuncia || '',
            patologiaTrazadora: item?.patologiaTrazadora || '',
            descripcionLesion: item?.descripcionLesion || ''
        })),
        medidasCorrectivas: mapRows(formulario?.medidasSeguimiento, (item) => {
            const rawMedidas = Array.isArray(item?.medidas)
                ? item.medidas
                : item?.nroMedida || item?.medidaCorrectiva || item?.fecha1 || item?.fecha2
                  ? [
                        {
                            nroMedida: item.nroMedida,
                            medidaCorrectiva: item.medidaCorrectiva,
                            fecha1: item.fecha1,
                            fecha2: item.fecha2
                        }
                    ]
                  : [];

            return {
                nroCausa: item?.numeroCausa ? String(item.numeroCausa) : '',
                hecho: item?.hecho || '',
                medidas: rawMedidas.map((medida: any) => ({
                    nroMedida: medida?.nroMedida ? String(medida.nroMedida) : '',
                    medidaCorrectiva: medida?.medidaCorrectiva || '',
                    fechaInicio: toInputDateValue(medida?.fecha1),
                    fechaFin: toInputDateValue(medida?.fecha2)
                }))
            };
        }),
        seguimientos:
            seguimientosDesdeMedidas.length > 0
                ? seguimientosDesdeMedidas
                : mapRows(formulario?.seguimientos, (item) => ({
                      medida: item?.idSegMedida ? String(item.idSegMedida) : '',
                      fechaPrevista: toInputDateValue(item?.fecha1),
                      fechaVerificacion: toInputDateValue(item?.fecha3),
                      resultado: item?.resultadoSeguimiento || ''
                  })),
        entrevistados: mapRows(formulario?.entrevistados, (item) => ({
            apellidoNombres: item?.nombre || '',
            tipoDocumento: inferInterviewedDocumentType(item?.cuil),
            dniCuil: item?.cuil || '',
            cargo: item?.cargo || '',
            fecha: toInputDateValue(item?.fecha),
            firmo: typeof item?.firmado === 'boolean' ? (item.firmado ? 'si' : 'no') : undefined
        }))
    };
};
